import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, CalendarDays } from "lucide-react";

// ============================================================================
// ShiftTemplatesAdmin — Schichtplan-Vorlagen für den Dienstplan
// Vorlage = vordefinierte Schicht (Name, Abteilung, Zeit, Dauer, Pause, Farbe)
// Wird im Dienstplan per Drag-Drop oder Quick-Add verwendet.
// ============================================================================
const EMPTY = {
  name: "", department_id: "", shift_type: "arbeit", einsatzort_id: "",
  workplace: "", start_time: "07:00", duration_minutes: 510, break_minutes: 30,
  color: "#3b82f6", notes: "",
};

export default function ShiftTemplatesAdmin() {
  const [items, setItems] = useState([]);
  const [depts, setDepts] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterDept, setFilterDept] = useState("");

  async function load() {
    try {
      const [t, d, w] = await Promise.all([
        api.get("/shift-templates", { params: filterDept ? { department_id: filterDept } : {} }),
        api.get("/departments"),
        api.get("/settings/workplaces"),
      ]);
      setItems(t.data); setDepts(d.data); setWorkplaces(w.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterDept]);

  async function save() {
    if (!form.name.trim()) { toast.error("Name ist Pflicht"); return; }
    if (!form.department_id) { toast.error("Abteilung ist Pflicht"); return; }
    try {
      if (editing) await api.put(`/shift-templates/${editing.id}`, form);
      else await api.post("/shift-templates", form);
      toast.success("Gespeichert");
      setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function remove(id) {
    if (!window.confirm("Vorlage löschen?")) return;
    await api.delete(`/shift-templates/${id}`);
    toast.success("Gelöscht"); load();
  }
  function startEdit(t) { setEditing(t); setForm({ ...EMPTY, ...t }); setShowForm(true); }
  function startNew() { setEditing(null); setForm(EMPTY); setShowForm(true); }

  const endTime = (t) => {
    const [h, m] = (t.start_time || "07:00").split(":").map(n => parseInt(n, 10));
    const total = h * 60 + m + (t.duration_minutes || 0);
    return `${String(Math.floor((total % 1440) / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  };
  const deptName = (id) => depts.find(d => d.id === id)?.name || "—";
  const deptColor = (id) => depts.find(d => d.id === id)?.color || "#3b82f6";

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md" data-testid="shift-templates-admin">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h4 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-blue-400" /> Schichtplan-Vorlagen
          </h4>
          <p className="text-xs text-zinc-500 mt-0.5">
            Vordefinierte Schichten für den Dienstplan — Name, Abteilung, Zeit, Dauer, Pause und Farbe.
            Werden im Dienstplan zur schnellen Schichtzuweisung verwendet.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)}
            data-testid="shift-templates-filter"
            className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100">
            <option value="">Alle Abteilungen</option>
            {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <span className="text-xs text-zinc-500">{items.length} Vorlage(n)</span>
          <button onClick={startNew} data-testid="shift-template-new"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded">
            <Plus className="w-3.5 h-3.5" /> Neue Schichtplan-Vorlage
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="p-10 text-center text-zinc-500 text-sm">
          <CalendarDays className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
          Noch keine Schichtplan-Vorlagen. Lege die erste an — z.B.{" "}
          <span className="text-zinc-300">„Amazon DVI3 ab 10:00 · 8:30h · 30 min Pause"</span>.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <th className="px-4 py-3 text-left">Name</th>
            <th className="px-4 py-3 text-left">Abteilung</th>
            <th className="px-4 py-3 text-left">Einsatzort</th>
            <th className="px-4 py-3 text-left">Zeit</th>
            <th className="px-4 py-3 text-right">Netto</th>
            <th className="px-4 py-3 text-right">Aktionen</th>
          </tr></thead>
          <tbody>{items.map(t => (
            <tr key={t.id} data-testid={`shift-template-row-${t.id}`} className="border-b border-zinc-800 hover:bg-zinc-800/40">
              <td className="px-4 py-2.5 text-zinc-100 font-medium">
                <div className="flex items-center gap-2">
                  <span className="w-1 h-5 rounded" style={{ backgroundColor: t.color || "#3b82f6" }}/>
                  {t.name}
                </div>
              </td>
              <td className="px-4 py-2.5">
                <span className="text-[10px] px-2 py-0.5 rounded text-white" style={{ backgroundColor: deptColor(t.department_id) }}>
                  {deptName(t.department_id)}
                </span>
              </td>
              <td className="px-4 py-2.5 text-zinc-300">{t.einsatzort_name || t.workplace || "—"}</td>
              <td className="px-4 py-2.5 text-zinc-300 font-mono">{t.start_time} – {endTime(t)}</td>
              <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-300">
                {Math.floor(((t.duration_minutes || 0) - (t.break_minutes || 0)) / 60)}h{((t.duration_minutes || 0) - (t.break_minutes || 0)) % 60 ? `${((t.duration_minutes || 0) - (t.break_minutes || 0)) % 60}m` : ""}
                <div className="text-[10px] text-zinc-500">inkl. {t.break_minutes || 0}m Pause</div>
              </td>
              <td className="px-4 py-2.5 text-right">
                <button onClick={() => startEdit(t)} data-testid={`shift-template-edit-${t.id}`}
                  className="text-xs text-blue-400 hover:text-blue-300 mr-3">Bearbeiten</button>
                <button onClick={() => remove(t.id)} className="text-xs text-red-400 hover:text-red-300">Löschen</button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="shift-template-form">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-xl">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-display font-semibold text-zinc-50">{editing ? "Vorlage bearbeiten" : "Neue Schichtplan-Vorlage"}</h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-400">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] uppercase text-zinc-500">Name *</label>
                <input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})}
                  placeholder="z.B. Amazon DVI3 ab 10:00"
                  data-testid="shift-template-name"
                  className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
              </div>
              <div>
                <label className="text-[10px] uppercase text-zinc-500">Abteilung *</label>
                <select value={form.department_id} onChange={(e)=>setForm({...form, department_id: e.target.value})}
                  data-testid="shift-template-dept"
                  className={`w-full mt-1 px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${!form.department_id ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-zinc-800"}`}>
                  <option value="">— Abteilung wählen —</option>
                  {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                {depts.length === 0 && (
                  <div className="mt-1 text-[10px] text-amber-400">
                    Noch keine Abteilungen angelegt. Gehe zu Einstellungen → Abteilungen.
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Schicht-Typ</label>
                  <select value={form.shift_type} onChange={(e)=>setForm({...form, shift_type: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                    <option value="arbeit">Arbeit</option><option value="frei">Frei</option>
                    <option value="urlaub">Urlaub</option><option value="krank">Krank</option>
                    <option value="schulung">Schulung</option><option value="buero">Büro</option><option value="homeoffice">Homeoffice</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Einsatzort</label>
                  <select
                    value={form.einsatzort_id || ""}
                    onChange={(e) => {
                      const id = e.target.value;
                      const wp = workplaces.find((w) => w.id === id);
                      setForm({ ...form, einsatzort_id: id, workplace: wp ? wp.name : form.workplace });
                    }}
                    data-testid="shift-template-einsatzort"
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                    <option value="">— Einsatzort wählen —</option>
                    {workplaces.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}{w.city ? ` · ${w.city}` : ""}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    Verknüpft mit „Einsatzorte". Neue Orte unter Einstellungen → Einsatzorte anlegen.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Startzeit</label>
                  <input type="time" value={form.start_time} onChange={(e)=>setForm({...form, start_time: e.target.value})}
                    data-testid="shift-template-start"
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Dauer (Min)</label>
                  <input type="number" value={form.duration_minutes} onChange={(e)=>setForm({...form, duration_minutes: +e.target.value})}
                    data-testid="shift-template-duration"
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
                  <div className="text-[10px] text-zinc-500 mt-1">= {Math.floor(form.duration_minutes/60)}h{form.duration_minutes%60 ? `${form.duration_minutes%60}m` : ""}</div>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Pause (Min)</label>
                  <input type="number" value={form.break_minutes} onChange={(e)=>setForm({...form, break_minutes: +e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
                </div>
              </div>
              <div className="grid grid-cols-[auto_1fr] gap-3 items-end">
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Farbe</label>
                  <input type="color" value={form.color} onChange={(e)=>setForm({...form, color: e.target.value})}
                    className="mt-1 w-16 h-10 bg-zinc-950 border border-zinc-800 rounded cursor-pointer"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Notiz (optional)</label>
                  <input value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
                </div>
              </div>
              <div className="bg-zinc-950 border border-zinc-800 rounded p-3 text-xs text-zinc-300">
                <div className="text-[10px] uppercase text-zinc-500 mb-1">Vorschau</div>
                <div><span className="font-semibold" style={{ color: form.color }}>{form.name || "Ohne Name"}</span></div>
                <div className="font-mono text-zinc-400">
                  {form.start_time} – {(() => {
                    const [h, m] = (form.start_time || "07:00").split(":").map(n=>parseInt(n,10));
                    const total = h*60+m+(form.duration_minutes||0);
                    return `${String(Math.floor((total%1440)/60)).padStart(2,"0")}:${String(total%60).padStart(2,"0")}`;
                  })()}
                  {" · "}Netto {Math.floor((form.duration_minutes-form.break_minutes)/60)}h{(form.duration_minutes-form.break_minutes)%60 ? `${(form.duration_minutes-form.break_minutes)%60}m` : ""} (inkl. {form.break_minutes}m Pause)
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={()=>setShowForm(false)} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={save} data-testid="shift-template-save" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
